import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const loadUser = async (uuid) => {
      try {
        const { data } = await api.initUser(uuid);
        setUser(data);
        setUserId(uuid);
      } catch (e) {
        console.error("Failed to load user:", e);
        toast.error("Failed to connect. Please refresh.");
      }
    };

    const init = async () => {
      try {
        // user_id is only ever set from a successful login — never auto-generated
        const storedUuid = localStorage.getItem("waimbow_user_id");
        const { data: status } = await api.getAuthStatus();

        if (!status.auth_enabled) {
          if (storedUuid) await loadUser(storedUuid);
          return;
        }

        try {
          await api.getMe();
        } catch {
          // Not authenticated — userId stays null → redirect to /login
          console.info("Not authenticated, redirecting to login");
          return;
        }

        if (!storedUuid) {
          // Valid session but no stable id — require login to derive it
          return;
        }
        await loadUser(storedUuid);
      } catch (e) {
        console.error("Init failed:", e);
        toast.error("Connection error. Please refresh.");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const updateName = useCallback(
    async (name) => {
      if (!userId) return;
      try {
        const { data } = await api.updateUser(userId, { name });
        setUser(data);
      } catch (e) {
        console.error("Failed to update name:", e);
        toast.error("Failed to update name");
      }
    },
    [userId]
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch (e) {
      console.warn("Logout request failed:", e);
    }
    setUser(null);
    setUserId(null);
    // Only clear the session token. Keep waimbow_user_id so data is always
    // findable on next login (identity is derived from the account email).
    localStorage.removeItem("waimbow_session");
    window.location.href = "/login";
  }, []);

  return (
    <UserContext.Provider value={{ user, userId, loading, updateName, logout }}>
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => useContext(UserContext);
